/**
 * Create and edit a project.
 *
 * The client is chosen on creation and **cannot be changed afterwards**, which
 * is why it is absent from the edit form rather than disabled in it. Moving a
 * project between clients would move its time, its tickets and its invoices
 * with it, and the API refuses — so offering the control would be offering
 * something that cannot happen.
 *
 * Money and rates are text on the way in and out. See `client-form-dialog.tsx`.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  ApiError,
  Alert,
  Button,
  Input,
  Modal,
  RecordSelect,
  Select,
  Switch,
  Textarea,
  api,
  toast,
} from '@connect/web-shared';

const MONEY = /^\d*\.?\d{0,2}$/;

const schema = z.object({
  name: z.string().trim().min(1, 'A project needs a name.').max(160),
  status: z.enum(['draft', 'active', 'on_hold', 'completed', 'cancelled']),
  billing_type: z.enum(['time_and_materials', 'fixed_price', 'retainer', 'non_billable']),
  starts_on: z.string().optional(),
  ends_on: z.string().optional(),
  budget_amount: z.string().trim().regex(MONEY, 'Enter an amount, to two decimal places.').optional(),
  // Entered in hours because that is how people talk about a budget, and
  // converted to minutes on the way out because that is what is stored.
  budget_hours: z.string().trim().regex(/^\d*$/, 'Whole hours.').optional(),
  hourly_rate: z.string().trim().regex(MONEY, 'Enter an amount, to two decimal places.').optional(),
  currency: z.string().trim().length(3, 'Use the three-letter currency code.'),
  description: z.string().trim().optional(),
  client_visible: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

interface ProjectRecord {
  public_id: string;
  code: string;
  name: string;
  status: string;
  billing_type: string;
  client_name: string;
  starts_on: string | null;
  ends_on: string | null;
  budget_amount: string | null;
  budget_minutes: number | null;
  hourly_rate: string | null;
  currency: string;
  description: string | null;
  client_visible: boolean;
}

const DEFAULTS: FormValues = {
  name: '',
  status: 'draft',
  billing_type: 'time_and_materials',
  starts_on: '',
  ends_on: '',
  budget_amount: '',
  budget_hours: '',
  hourly_rate: '',
  currency: 'INR',
  description: '',
  client_visible: false,
};

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const BILLING_OPTIONS = [
  { value: 'time_and_materials', label: 'Time & materials' },
  { value: 'fixed_price', label: 'Fixed price' },
  { value: 'retainer', label: 'Retainer' },
  { value: 'non_billable', label: 'Non-billable' },
];

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD'].map((code) => ({
  value: code,
  label: code,
}));

export function ProjectFormDialog({
  open,
  onClose,
  onCreated,
  publicId,
  /** Pre-selects the client — for the "new project" button on a client's page. */
  clientPublicId,
  clientName,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (publicId: string) => void;
  publicId?: string;
  clientPublicId?: string;
  clientName?: string;
}) {
  const queryClient = useQueryClient();
  const editing = Boolean(publicId);
  const [client, setClient] = useState<string | null>(clientPublicId ?? null);

  const existing = useQuery({
    queryKey: ['projects', publicId],
    queryFn: () => api.get<ProjectRecord>(`/projects/${publicId}`),
    enabled: open && editing,
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  useEffect(() => {
    if (!open) return;
    if (editing && existing.data) {
      const row = existing.data;
      form.reset({
        ...DEFAULTS,
        name: row.name,
        status: row.status as FormValues['status'],
        billing_type: row.billing_type as FormValues['billing_type'],
        starts_on: row.starts_on ?? '',
        ends_on: row.ends_on ?? '',
        budget_amount: row.budget_amount ?? '',
        budget_hours: row.budget_minutes ? String(Math.round(row.budget_minutes / 60)) : '',
        hourly_rate: row.hourly_rate ?? '',
        currency: row.currency,
        description: row.description ?? '',
        client_visible: row.client_visible,
      });
    } else if (!editing) {
      form.reset(DEFAULTS);
      setClient(clientPublicId ?? null);
    }
  }, [open, editing, existing.data, form, clientPublicId]);

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const { budget_hours, ...rest } = values;
      const payload: Record<string, unknown> = Object.fromEntries(
        Object.entries(rest).map(([key, value]) => [
          key,
          typeof value === 'string' && value.trim() === '' ? null : value,
        ]),
      );
      payload.budget_minutes = budget_hours ? Number(budget_hours) * 60 : null;

      if (editing) return api.patch<ProjectRecord>(`/projects/${publicId}`, payload);
      return api.post<ProjectRecord>(`/projects/for-client/${client}`, payload);
    },
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success(
        editing ? `${record.name} updated` : `${record.name} created`,
        `Code ${record.code}`,
      );
      onCreated?.(record.public_id);
      if (editing) onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        let placed = false;
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          if (field in DEFAULTS) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        if (!placed) form.setError('root', { message: error.message });
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  const submit = () => {
    if (!editing && client === null) {
      form.setError('root', { message: 'Choose the client this project is for.' });
      return;
    }
    void form.handleSubmit((values) => save.mutateAsync(values))();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit project' : 'New project'}
      description={
        editing ? undefined : 'The code is generated. Everything but the name can wait.'
      }
      size="lg"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button size="sm" loading={save.isPending} onClick={submit}>
            {editing ? 'Save changes' : 'Create project'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {form.formState.errors.root?.message ? (
          <Alert tone="danger">{form.formState.errors.root.message}</Alert>
        ) : null}

        {editing ? (
          <Alert tone="info">
            {existing.data?.client_name
              ? `For ${existing.data.client_name}.`
              : 'The client a project belongs to cannot be changed.'}{' '}
            Time, tickets and invoices are recorded against it.
          </Alert>
        ) : (
          <RecordSelect
            label="Client"
            required
            endpoint="/crm/clients"
            value={client}
            valueLabel={clientName}
            onChange={(next) => setClient(next)}
            toOption={(row) => ({
              value: String(row.public_id),
              label: String(row.name),
              detail: String(row.code ?? ''),
            })}
            placeholder="Search clients…"
            hint="Cannot be changed once the project exists."
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Name"
            required
            containerClassName="sm:col-span-2"
            error={form.formState.errors.name?.message}
            {...form.register('name')}
          />
          <Select
            label="Status"
            options={STATUS_OPTIONS}
            error={form.formState.errors.status?.message}
            {...form.register('status')}
          />
          <Select
            label="Billing"
            options={BILLING_OPTIONS}
            hint="Non-billable stops time on it reaching an invoice."
            error={form.formState.errors.billing_type?.message}
            {...form.register('billing_type')}
          />
          <Input
            label="Starts"
            type="date"
            error={form.formState.errors.starts_on?.message}
            {...form.register('starts_on')}
          />
          <Input
            label="Ends"
            type="date"
            error={form.formState.errors.ends_on?.message}
            {...form.register('ends_on')}
          />
          <Input
            label="Budget"
            inputMode="decimal"
            placeholder="0.00"
            error={form.formState.errors.budget_amount?.message}
            {...form.register('budget_amount')}
          />
          <Select
            label="Currency"
            options={CURRENCIES}
            error={form.formState.errors.currency?.message}
            {...form.register('currency')}
          />
          <Input
            label="Budget hours"
            inputMode="numeric"
            hint="Stored as minutes. Whole hours are enough here."
            error={form.formState.errors.budget_hours?.message}
            {...form.register('budget_hours')}
          />
          <Input
            label="Hourly rate"
            inputMode="decimal"
            placeholder="0.00"
            hint="Falls back to the client's rate when empty."
            error={form.formState.errors.hourly_rate?.message}
            {...form.register('hourly_rate')}
          />
          <Textarea
            label="Description"
            rows={3}
            containerClassName="sm:col-span-2"
            error={form.formState.errors.description?.message}
            {...form.register('description')}
          />
        </div>

        <Switch
          label="Visible to the client"
          hint="Lets people at this client see the project name against their tickets and invoices."
          {...form.register('client_visible')}
        />
      </form>
    </Modal>
  );
}
